import React, { useState, useEffect } from 'react';
import { Collapse, Select, Row, Col, Space, Modal, Spin, message, Button as AntdButton, InputNumber } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { Colors } from '../../config';
import Button from '../Button';
import RoleCheck from '../RoleCheck/RoleCheck';
import { getQrMeta } from '../../config/services/patrolService';
import ApiCaller from '../../config/api';

import Text from '../Text';
import useColors from '../../hooks/useColors';
const { Panel } = Collapse;

const MAX_QR_COPIES_PER_PAGE = 20;

// Lays `copies` copies of the same QR image out in a grid so up to
// MAX_QR_COPIES_PER_PAGE of them fit on a single printed page.
const buildQrGridHtml = (imgUrl, copies) => {
    const count = Math.min(Math.max(parseInt(copies, 10) || 1, 1), MAX_QR_COPIES_PER_PAGE);
    const imgs = Array.from({ length: count }).map(() => `<img src="${imgUrl}" />`).join('');
    return `<!DOCTYPE html><html><head><title>Print QR Code</title>
<style>
  body { margin: 0; padding: 16px; }
  .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
  .grid img { width: 100%; aspect-ratio: 1 / 1; object-fit: contain; }
</style>
</head><body><div class="grid">${imgs}</div></body></html>`;
};

// ── Inline QR thumbnail fetched per checkpoint row ───────────
const CheckpointQrThumb = ({ item, onOpenPrint }) => {
    const [qrUrl, setQrUrl] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let objectUrl;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const meta = await getQrMeta(item?.id);
                if (!meta?.pngUrl) throw new Error('No QR image URL returned');
                // pngUrl points at our API, so it must be fetched with the auth
                // header rather than loaded directly via <img src>, which would
                // hit the endpoint unauthenticated and render a broken image.
                const res = await ApiCaller.GetBlob(meta.pngUrl);
                if (cancelled) return;
                objectUrl = URL.createObjectURL(res.data);
                setQrUrl(objectUrl);
            } catch (err) {
                if (!cancelled) setQrUrl(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [item?.id]);

    return (
        <div className="flex items-center gap-2">
            {loading ? (
                <Spin size="small" />
            ) : qrUrl ? (
                <img src={qrUrl} alt="QR Code" style={{ width: 64, height: 64 }} />
            ) : (
                <span className="text-xs text-gray-400">QR unavailable</span>
            )}
            <AntdButton
                type="primary"
                shape="circle"
                size="small"
                icon={<PrinterOutlined />}
                disabled={loading}
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenPrint(item);
                }}
            />
        </div>
    );
};

const CollapseBar = ({
    onChange,
    genExtra,
    text,
    items,
    label,
    data,
    ...props

}) => {
    const { colorPrimary } = useColors();

    const [printModal, setPrintModal] = useState({ visible: false, pngUrl: null, name: '' });
    const [printCopies, setPrintCopies] = useState(1);

    const openPrintModal = async (item) => {
        setPrintCopies(1);
        setPrintModal({ visible: true, pngUrl: null, name: item?.name });
        try {
            const meta = await getQrMeta(item?.id);
            if (!meta?.pngUrl) throw new Error('No QR image URL returned');
            const res = await ApiCaller.GetBlob(meta.pngUrl);
            const blobUrl = URL.createObjectURL(res.data);
            setPrintModal({ visible: true, pngUrl: blobUrl, name: item?.name });
        } catch (err) {
            message.error('Failed to load QR code.');
            setPrintModal({ visible: false, pngUrl: null, name: '' });
        }
    };

    const closePrintModal = () => {
        if (printModal.pngUrl) URL.revokeObjectURL(printModal.pngUrl);
        setPrintModal({ visible: false, pngUrl: null, name: '' });
    };

    const handlePrint = () => {
        if (!printModal.pngUrl) return;
        const iframe = document.createElement('iframe');
        Object.assign(iframe.style, {
            position: 'fixed',
            right: '0',
            bottom: '0',
            width: '0',
            height: '0',
            border: '0',
        });
        iframe.onload = () => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        };
        iframe.srcdoc = buildQrGridHtml(printModal.pngUrl, printCopies);
        document.body.appendChild(iframe);
        window.addEventListener('focus', function cleanup() {
            iframe.remove();
            window.removeEventListener('focus', cleanup);
        }, { once: true });
    };

    return (
        <>
            <Row className='w-full'>
                {data && data?.map(item => (
                    <Collapse
                        accordion
                        className='mb-4 w-full'
                        expandIconPosition='end'
                        size="large"
                        key={item?.id}
                    >
                        <Panel
                            className=''
                            key={item.id}
                            header={
                                <Row gutter={[16, 16]} className='rounded-lg w-full'  >
                                    <Col lg={3} md={8} sm={24} xs={24}>
                                        <Text.Label
                                            style={{ color: Colors.LabelColor }}
                                            text={'Checkpoint Name'}
                                        />
                                        <p className='text-xs'>{item?.name}</p>
                                    </Col>
                                    <Col lg={5} md={8} sm={24} xs={24}>
                                        <Text.Label
                                            style={{ color: Colors.LabelColor }}
                                            text={'Checkpoint Location'}
                                        />
                                        <p className='text-xs break-all'>{item?.location}</p>
                                    </Col>
                                    <Col lg={3} md={8} sm={24} xs={24}>
                                        <Text.Label
                                            style={{ color: Colors.LabelColor }}
                                            text={'NFC Code Value'}
                                        />
                                        <p className='text-xs'>{item?.code}</p>
                                    </Col>

                                    <Col lg={5} md={8} sm={24} xs={24}>
                                        <Text.Label
                                            style={{ color: Colors.LabelColor }}
                                            text={'Property Address'}
                                        />
                                        <p className='text-xs break-all'>{`${item?.address?.streetNumber}, ${item?.address?.streetName}, ${item?.address?.city} ${item?.address?.zip}`}</p>
                                    </Col>

                                    <Col lg={4} md={24} sm={24} xs={24}>
                                        <CheckpointQrThumb item={item} onOpenPrint={openPrintModal} />
                                    </Col>

                                    <RoleCheck
                                        children={
                                            <Col span={4} lg={4} md={24} sm={24} xs={24} className='flex justify-start md:justify-end'>
                                                <Button.Basic
                                                    className="w-full px-6 h-10 border-none text-white border-transparent"
                                                    style={{ backgroundColor: colorPrimary }}
                                                    text={"Edit"}
                                                    onClick={() => onChange(item)}
                                                />
                                            </Col>
                                        }
                                        task={'Edit'}
                                    />

                                    <br />

                                </Row>
                            }
                        >
                            <Row gutter={[16, 16]} className='mb-6 mx-2 bg-white rounded-lg'>
                                <Col span={24} className='rounded-lg'>
                                    <Row className='' gutter={[16, 16]}>
                                        <Col lg={8} md={8} sm={12} xs={24}>
                                            <Text.Label
                                                style={{ color: Colors.LabelColor }}
                                                text={'Checkpoint Unit'}
                                            />
                                            <p className='text-xs'>{item?.unit}</p>
                                        </Col>
                                        <Col lg={8} md={8} sm={12} xs={24}>
                                            <Text.Label
                                                style={{ color: Colors.LabelColor }}
                                                text={'Checkpoint Incident Type'}
                                            />
                                            <p className='text-xs break-all'>{item?.incidentType?.name}</p>
                                        </Col>
                                        {/* <Col lg={8} md={24} sm={24} xs={24}>
                                            <Text.Label
                                                style={{ color: Colors.LabelColor }}
                                                text={'Auto Create Issue Type'}
                                            />
                                            <p className='text-xs'>{'-'}</p>
                                        </Col> */}

                                        <Col lg={24} md={24} sm={24} xs={24}>
                                            <Text.Label
                                                style={{ color: Colors.LabelColor }}
                                                text={'Report Notes'}
                                            />
                                            <p className='text-xs break-all'>{item?.reportNotes}</p>
                                        </Col>

                                        <Col lg={24} md={24} sm={24} xs={24}>
                                            <Text.Label
                                                style={{ color: Colors.LabelColor }}
                                                text={'Officer Notes'}
                                            />
                                            <p className='text-xs break-all'>{item?.officerNotes}</p>
                                        </Col>
                                    </Row>
                                    <br />
                                </Col>
                            </Row>
                        </Panel>

                    </Collapse>
                ))}
            </Row>

            <Modal
                title={`QR Code${printModal.name ? ' - ' + printModal.name : ''}`}
                open={printModal.visible}
                onCancel={closePrintModal}
                footer={[
                    <Button.Basic
                        key="print"
                        className="px-6 h-10 border-none text-white border-transparent"
                        style={{ backgroundColor: colorPrimary }}
                        icon={<PrinterOutlined />}
                        text={"Print"}
                        disabled={!printModal.pngUrl}
                        onClick={handlePrint}
                    />,
                ]}
                destroyOnClose
            >
                <div className="flex flex-col items-center" style={{ minHeight: 220 }}>
                    {printModal.pngUrl ? (
                        <img src={printModal.pngUrl} alt="QR Code" style={{ width: 220, height: 220 }} />
                    ) : (
                        <Spin size="large" />
                    )}

                    <div className="flex items-center gap-2 mt-4">
                        <Text.Label
                            style={{ color: Colors.LabelColor }}
                            text={`QR codes per page (max ${MAX_QR_COPIES_PER_PAGE})`}
                        />
                        <InputNumber
                            min={1}
                            max={MAX_QR_COPIES_PER_PAGE}
                            value={printCopies}
                            onChange={(val) => setPrintCopies(Math.min(Math.max(val || 1, 1), MAX_QR_COPIES_PER_PAGE))}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default CollapseBar;
